import { getCourierToken } from "@/api/courier";
import { useCourier } from "@trycourier/courier-react";
import { CourierInboxDatastore, CourierInboxDataStoreListener, type CourierInboxFeedType, type InboxDataSet, type InboxMessage } from "@trycourier/courier-ui-inbox";
import React, { createContext, useContext, useEffect, useRef } from "react";

export interface CourierContextType {
  courierInitialized: boolean;
  unreadCount: number;
  isLoading: boolean;
}

const CourierContext = createContext<CourierContextType | undefined>(undefined);

export const useCourierContext = () => {
  const context = useContext(CourierContext);
  if (context === undefined) {
    throw new Error("useCourierContext must be used within a CourierProvider");
  }
  return context;
};

interface CourierProviderProps {
  children: React.ReactNode;
  userId: string | null;
}

export const CourierProvider: React.FC<CourierProviderProps> = ({
  children,
  userId,
}) => {
  const courier = useCourier();
  const [courierInitialized, setCourierInitialized] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const listenerRef = useRef<CourierInboxDataStoreListener | null>(null);

  useEffect(() => {
    if (userId) {
      getCourierToken()
        .then((token) => {
          console.log("Courier token retrieved for user");
          courier.shared.signIn({
            userId: userId,
            jwt: token,
          });
          setCourierInitialized(true);

          // Create and register the listener for unread count changes
          const listener = new CourierInboxDataStoreListener({
            onError: (error: Error): void => {
              console.error(error);
            },
            onDataSetChange: (dataSet: InboxDataSet, feedType: CourierInboxFeedType): void => {
              console.log("DataSet changed", dataSet, feedType);
            },
            onPageAdded: (dataSet: InboxDataSet, feedType: CourierInboxFeedType): void => {
              console.log("Page added", dataSet, feedType);
            },
            // onMessageAdd: (message: InboxMessage, index: number, feedType: CourierInboxFeedType): void => {
            //   console.log("Message added", message, index, feedType);

            //   // Show toast notification for new message
            //   if (message.title || message.preview) {
            //     toast(message.title || "New message", {
            //       description: message.preview || "You have a new message",
            //       duration: 4000,
            //     });
            //   }
            // },
            onMessageRemove: (
              message: InboxMessage,
              index: number,
              feedType: CourierInboxFeedType,
            ): void => {
              console.log("Message removed", message, index, feedType);
            },
            onMessageUpdate: (
              message: InboxMessage,
              index: number,
              feedType: CourierInboxFeedType,
            ): void => {
              console.log("Message updated", message, index, feedType);
            },
            onUnreadCountChange: (unreadCount: number): void => {
              console.log("Unread count changed", unreadCount);
              setUnreadCount(unreadCount);
            },
          });

          listenerRef.current = listener;
          CourierInboxDatastore.shared.addDataStoreListener(listener);

          // Load the inbox automatically
          const loadInboxData = async () => {
            setIsLoading(true);
            await CourierInboxDatastore.shared.load({ canUseCache: true });
            await CourierInboxDatastore.shared.listenForUpdates();
            setIsLoading(false);
          };

          loadInboxData().catch((error) => {
            console.error("Error loading inbox:", error);
            setIsLoading(false);
          });
        })
        .catch((error) => {
          console.error("Error retrieving Courier token:", error);
          setCourierInitialized(false);
        });
    } else {
      setCourierInitialized(false);
      setUnreadCount(0);
    }

    // Cleanup function to remove listener when user changes or component unmounts
    return () => {
      if (listenerRef.current) {
        CourierInboxDatastore.shared.removeDataStoreListener(listenerRef.current);
        listenerRef.current = null;
      }
    };
  }, [userId]);

  const value: CourierContextType = {
    courierInitialized,
    unreadCount,
    isLoading,
  };

  return (
    <CourierContext.Provider value={value}>
      {children}
    </CourierContext.Provider>
  );
};
